import React from 'react';

import ListFilter from '../../components/ListFilter';
import CareManagerFilter from './OrganizationMemberList/filter/CareManagerFilter';
import DeviceFilter from './OrganizationMemberList/filter/DeviceFilter';

export type MemberListFilters = {
    // Inline filter sections rendered in the header search filters popover.
    filters: React.ReactNode;
    // URL query keys that count toward the active-filter badge.
    filterParams: string[];
};

// Composes the filter sections + active-filter param keys for each member list
// tab. The filters are rendered inside the shared header search popover, so each
// section is rendered `inline` (heading + options, no own dropdown).
const getMemberListFilters = (listVersion?: string): MemberListFilters => {
    switch (listVersion) {
        case 'discharged_and_cancelled':
            return {
                filters: <CareManagerFilter inline />,
                filterParams: ['care_manager_ids']
            };

        case 'disengaged':
            return {
                filters: (
                    <>
                        <CareManagerFilter inline />
                        <ListFilter
                            inline
                            options={[
                                { value: 'DISCONNECTED', label: 'Offline', dividerAfter: true },
                                { value: 'EXCHANGED', label: 'Replacement' },
                                { value: 'DEVICE_RESET', label: 'Factory Reset' },
                                { value: 'NO_CONFIG', label: 'Setup needed' },
                                { value: 'NO_PILLS', label: 'No pills' },
                                { value: 'NO_SCHEDULES', label: 'No schedules' },
                                { value: 'IN_VACATION', label: 'In vacation' },
                                { value: 'TRANSPORT_MODE', label: 'In transport' },
                                { value: 'NO_ADHERENCE', label: 'Missing doses' }
                            ]}
                            searchParam="disengaged_reason"
                            label="Reason"
                            mode="single"
                            variant="pills"
                            hasResetBtn
                            resetPaginationKeys={['offset']}
                        />
                    </>
                ),
                filterParams: ['care_manager_ids', 'disengaged_reason']
            };

        case 'checkin-members':
            return {
                filters: (
                    <>
                        <CareManagerFilter inline />
                        <ListFilter
                            inline
                            options={[
                                { value: 'overdue', label: 'Overdue' },
                                { value: 'due', label: 'Due' },
                                { value: 'completed', label: 'Completed' }
                            ]}
                            searchParam="checkin_status"
                            label="Status"
                            mode="multi"
                            variant="pills"
                            hasResetBtn
                            persistToLocalStorage
                            resetPaginationKeys={['offset']}
                        />
                        <ListFilter
                            inline
                            options={[
                                { value: 'scheduled', label: 'Scheduled' },
                                { value: 'default', label: 'Default' }
                            ]}
                            searchParam="checkin_date"
                            label="Date"
                            mode="single"
                            variant="pills"
                            hasResetBtn
                            resetPaginationKeys={['offset']}
                        />
                    </>
                ),
                filterParams: ['care_manager_ids', 'checkin_status', 'checkin_date']
            };

        default:
            // Active list
            return {
                filters: (
                    <>
                        <CareManagerFilter inline />
                        <DeviceFilter inline />
                        <ListFilter
                            inline
                            options={[
                                { value: 'due', label: 'Due' },
                                { value: 'completed', label: 'Completed' }
                            ]}
                            searchParam="cm_checkin_status"
                            label="Monthly Check-in"
                            mode="single"
                            variant="pills"
                            hasResetBtn
                            resetPaginationKeys={['offset']}
                        />
                    </>
                ),
                filterParams: [
                    'care_manager_ids',
                    'online_status',
                    'device_status',
                    'cm_checkin_status'
                ]
            };
    }
};

export default getMemberListFilters;
